import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Label } from "@gigpro/ui/Label";
import { RadioGroup, RadioGroupItem } from "@gigpro/ui/RadioGroup";
import { Textarea } from "@gigpro/ui/Textarea";
import type { GigOutreachReasonEnum } from "@sidegigllc/api/client";
import React from "react";

export interface DirectOfferNote {
  reason: GigOutreachReasonEnum;
  note: string;
}

interface DirectOfferNoteDialogProps {
  open: boolean;
  /** Who the offer goes to, e.g. "Ana B." or "37 pros". */
  recipients: string;
  onCancel: () => void;
  onConfirm: (note: DirectOfferNote) => void;
}

const REASONS: ReadonlyArray<[GigOutreachReasonEnum, string]> = [
  ["VENUE_REQUESTED", "Venue asked for these pros"],
  ["NOT_OPEN_TO_EVERYONE", "Shift isn't open to everyone"],
  ["LAST_CALL_DID_NOT_FILL", "Last Call didn't fill it"],
  ["PRO_REQUESTED", "Pro asked for this shift"],
  ["OTHER", "Other"],
];
const NOTE_MIN_LENGTH = 10;
const NOTE_MAX_LENGTH = 500;

export function DirectOfferNoteDialog(props: Readonly<DirectOfferNoteDialogProps>) {
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DirectOfferNoteDialogBody {...props} />
    </Dialog>
  );
}

function DirectOfferNoteDialogBody({ recipients, onCancel, onConfirm }: Readonly<DirectOfferNoteDialogProps>) {
  const [reason, setReason] = React.useState<GigOutreachReasonEnum | null>(null);
  const [note, setNote] = React.useState("");

  const noteLength = note.trim().length;
  const canSend = reason !== null && noteLength >= NOTE_MIN_LENGTH;

  const onSend = () => {
    if (reason === null || !canSend) return;
    onConfirm({ reason, note: note.trim() });
  };

  return (
    <>
      <DialogTitle>Send a direct offer to {recipients}?</DialogTitle>
      <p className="text-normal text-gray">Why a direct offer for this shift? Your answer is saved with the send.</p>
      <RadioGroup
        aria-label="Reason for the direct offer"
        className="py-2"
        value={reason ?? ""}
        onValueChange={(value) => setReason(value as GigOutreachReasonEnum)}
      >
        {REASONS.map(([value, label]) => (
          <ReasonOption key={value} value={value} label={label} />
        ))}
      </RadioGroup>
      <Textarea
        aria-label="Note"
        placeholder="e.g. GM asked for Ana and Ben by name"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={NOTE_MAX_LENGTH}
        required
      />
      <p className="text-small text-gray">
        {noteLength < NOTE_MIN_LENGTH
          ? `At least ${NOTE_MIN_LENGTH} characters (${noteLength}/${NOTE_MIN_LENGTH})`
          : `${note.length}/${NOTE_MAX_LENGTH}`}
      </p>
      <DialogFooter>
        <Button className="w-full" onClick={onCancel}>
          Back
        </Button>
        <Button className="w-full" variant="contained" onClick={onSend} disabled={!canSend}>
          Continue
        </Button>
      </DialogFooter>
    </>
  );
}

function ReasonOption({ value, label }: Readonly<{ value: GigOutreachReasonEnum; label: string }>) {
  const id = `direct-offer-reason-${value}`;
  return (
    <div className="flex items-center">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="ml-2 text-normal font-semibold">
        {label}
      </Label>
    </div>
  );
}
